import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Users, 
  UserCheck, 
  Calendar as CalendarIcon, 
  Briefcase, 
  DollarSign, 
  Loader2,
  Clock,
  ArrowRight,
  Edit,
  Save,
  Trash2,
  Video,
  Building,
  Phone,
  Mail,
  MapPin
} from "lucide-react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { formatTime12Hour, formatShortDateMST } from "@/utils/timeUtils";
import { format } from "date-fns";
import { 
  MEETING_TYPES, 
  MEETING_FORMATS,
  getMeetingTypeLabel, 
  getMeetingTypeColor,
  getMeetingFormatLabel,
  getMeetingFormatColor,
  DEFAULT_MEETING_FORMAT
} from "@/constants/meetingTypes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ title, value, icon: Icon, description }) => (
  <Card className="bg-navy-900/50 border-slate-800 hover:border-primary/30 transition-all">
    <CardHeader className="flex flex-row items-center justify-between pb-2">
      <CardTitle className="text-sm font-medium text-slate-400">{title}</CardTitle>
      <Icon className="w-5 h-5 text-primary" />
    </CardHeader>
    <CardContent>
      <div className="text-3xl font-heading font-bold text-white">{value}</div>
      {description && <p className="text-xs text-slate-500 mt-1">{description}</p>}
    </CardContent>
  </Card>
);

const AdminDashboard = () => {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);
  const [allAppointments, setAllAppointments] = useState([]);
  const [leads, setLeads] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Edit Modal State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [editDate, setEditDate] = useState(null);
  const [editTime, setEditTime] = useState("");
  const [editDuration, setEditDuration] = useState(30);
  const [editMeetingType, setEditMeetingType] = useState("courtesy");
  const [editMeetingFormat, setEditMeetingFormat] = useState("zoom");
  const [editStatus, setEditStatus] = useState("scheduled");

  // Delete Confirmation State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = async () => {
    try {
      const [statsRes, appointmentsRes, leadsRes] = await Promise.all([
        axios.get(`${API}/admin/dashboard-stats`, { headers }),
        axios.get(`${API}/admin/appointments`, { headers }),
        axios.get(`${API}/admin/leads`, { headers })
      ]);
      setStats(statsRes.data);
      setLeads(leadsRes.data);
      setAllAppointments(appointmentsRes.data);
      
      // Fetch available slots separately (don't fail if not available)
      try {
        const slotsRes = await axios.get(`${API}/scheduling/slots`, { headers });
        // Ensure we always get an array - API may return {available: false} when mocked
        setAvailableSlots(Array.isArray(slotsRes.data) ? slotsRes.data : []);
      } catch (e) {
        console.log("Available slots endpoint not available");
        setAvailableSlots([]);
      }
      
      // Filter to only upcoming appointments (scheduled status and future date)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const upcoming = appointmentsRes.data
        .filter(apt => {
          const aptDate = new Date(apt.date);
          return apt.status === "scheduled" && aptDate >= today;
        })
        .sort((a, b) => {
          const dateA = new Date(`${a.date}T${a.time}`);
          const dateB = new Date(`${b.date}T${b.time}`);
          return dateA - dateB;
        })
        .slice(0, 10);
      
      setUpcomingAppointments(upcoming);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [token]);

  // Get full contact info for a lead/client ID
  const getContactInfo = (leadId, clientId) => {
    const id = leadId || clientId;
    const lead = leads.find(l => l.id === id);
    if (lead) {
      const name = lead.first_name && lead.last_name 
        ? `${lead.first_name} ${lead.last_name}`
        : lead.contact_name || lead.email || "Unknown";
      
      // Build address string
      let address = "";
      if (lead.business_address) {
        address = lead.business_address;
        if (lead.suite_number) address += `, ${lead.suite_number}`;
      }
      
      return { 
        name, 
        business: lead.business_name || "",
        email: lead.email || "",
        telephone: lead.telephone || "",
        address: address,
        city: lead.city || "",
        state: lead.state || ""
      };
    }
    return { name: "Unknown", business: "", email: "", telephone: "", address: "", city: "", state: "" };
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return "Tomorrow";
    } else {
      return date.toLocaleDateString("en-US", { 
        weekday: "short", 
        month: "short", 
        day: "numeric" 
      });
    }
  };

  // Get available times for a specific date
  const getTimesForDate = (date) => {
    if (!date || !Array.isArray(availableSlots)) return [];
    const dateStr = format(date, "yyyy-MM-dd");
    return availableSlots
      .filter(slot => slot.date === dateStr)
      .map(slot => slot.time)
      .sort();
  };

  // Open edit modal
  const openEditModal = (apt) => {
    setEditingAppointment(apt);
    setEditDate(new Date(apt.date));
    setEditTime(apt.time);
    setEditDuration(apt.duration || 30);
    setEditMeetingType(apt.meeting_type || "courtesy");
    setEditMeetingFormat(apt.meeting_format || "zoom");
    setEditStatus(apt.status || "scheduled");
    setEditModalOpen(true);
  };

  // Handle update
  const handleUpdate = async () => {
    if (!editingAppointment || !editDate || !editTime) {
      toast.error("Please fill all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.put(`${API}/admin/appointments/${editingAppointment.id}`, {
        date: format(editDate, "yyyy-MM-dd"),
        time: editTime,
        duration: editDuration,
        meeting_type: editMeetingType,
        meeting_format: editMeetingFormat,
        status: editStatus
      }, { headers });
      
      toast.success("Appointment updated");
      setEditModalOpen(false);
      setEditingAppointment(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update appointment");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!editingAppointment) return;

    setIsSubmitting(true);
    try {
      await axios.delete(`${API}/admin/appointments/${editingAppointment.id}`, { headers });
      toast.success("Appointment deleted");
      setDeleteModalOpen(false);
      setEditModalOpen(false);
      setEditingAppointment(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete appointment");
    } finally {
      setIsSubmitting(false);
    }
  };

  const timesForEditDate = getTimesForDate(editDate);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <StatCard
          title="Total Leads"
          value={stats?.leads || 0}
          icon={Users}
          description="Potential clients"
        />
        <StatCard
          title="Active Clients"
          value={stats?.clients || 0}
          icon={UserCheck}
          description="Converted leads"
        />
        <StatCard
          title="Scheduled"
          value={stats?.scheduled_appointments || 0}
          icon={CalendarIcon}
          description="Upcoming sessions"
        />
        <StatCard
          title="Active Engagements"
          value={stats?.active_engagements || 0}
          icon={Briefcase}
          description="In progress"
        />
        <StatCard
          title="Revenue"
          value={`$${(stats?.total_revenue || 0).toLocaleString()}`}
          icon={DollarSign}
          description="Total collected"
        />
      </div>

      {/* Upcoming Appointments */}
      <Card className="bg-navy-900/50 border-slate-800">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-white">
            <CalendarIcon className="w-5 h-5 text-primary" />
            Upcoming Appointments
          </CardTitle>
          <Link to="/admin/appointments">
            <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white">
              View All
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </CardHeader>
        <CardContent>
          {upcomingAppointments.length > 0 ? (
            <div className="space-y-3">
              {upcomingAppointments.map((apt, index) => {
                const contactInfo = getContactInfo(apt.lead_id, apt.client_id);
                const isToday = formatDate(apt.date) === "Today";
                const isZoom = apt.meeting_format !== "in_person";
                
                return (
                  <div
                    key={apt.id || index}
                    onClick={() => openEditModal(apt)}
                    className={`p-4 rounded-lg border transition-all cursor-pointer hover:border-primary/50 ${
                      isToday 
                        ? "bg-primary/10 border-primary/30" 
                        : "bg-navy-950/50 border-slate-800"
                    }`}
                    data-testid={`upcoming-apt-${index}`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Date/Time Column */}
                      <div className="flex-shrink-0 w-20 text-center">
                        <p className={`text-xs font-medium ${isToday ? "text-primary" : "text-slate-400"}`}>
                          {formatDate(apt.date)}
                        </p>
                        <p className="text-lg font-heading font-bold text-white">
                          {formatTime12Hour(apt.time)}
                        </p>
                      </div>

                      {/* Divider */}
                      <div className={`w-px h-full min-h-[60px] ${isToday ? "bg-primary/30" : "bg-slate-700"}`} />

                      {/* Contact Info & Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="text-white font-medium truncate">
                              {contactInfo.name}
                            </p>
                            <p className="text-sm text-slate-400 truncate">
                              {contactInfo.business}
                            </p>
                          </div>
                          
                          {/* Badges */}
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <div className="flex items-center gap-1.5 text-xs text-slate-400">
                              <Clock className="w-3.5 h-3.5" />
                              {apt.duration || 30}m
                            </div>
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getMeetingTypeColor(apt.meeting_type)}`}>
                              {getMeetingTypeLabel(apt.meeting_type)}
                            </span>
                          </div>
                        </div>
                        
                        {/* Meeting Format & Contact Details */}
                        <div className={`mt-2 pt-2 border-t ${isToday ? "border-primary/20" : "border-slate-700/50"}`}>
                          <div className="flex items-center gap-2 mb-1">
                            {isZoom ? (
                              <span className="inline-flex items-center gap-1 text-xs text-purple-400">
                                <Video className="w-3.5 h-3.5" />
                                Zoom Meeting
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs text-orange-400">
                                <Building className="w-3.5 h-3.5" />
                                In-Person
                              </span>
                            )}
                          </div>
                          
                          {/* Contact Details based on format */}
                          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                            {isZoom ? (
                              // Zoom: Show phone and email
                              <>
                                {contactInfo.telephone && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    {contactInfo.telephone}
                                  </span>
                                )}
                                {contactInfo.email && (
                                  <span className="flex items-center gap-1">
                                    <Mail className="w-3 h-3" />
                                    {contactInfo.email}
                                  </span>
                                )}
                              </>
                            ) : (
                              // In-Person: Show address, city, and phone
                              <>
                                {(contactInfo.address || contactInfo.city) && (
                                  <span className="flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    {contactInfo.address}{contactInfo.address && contactInfo.city ? ", " : ""}{contactInfo.city}{contactInfo.state ? `, ${contactInfo.state}` : ""}
                                  </span>
                                )}
                                {contactInfo.telephone && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    {contactInfo.telephone}
                                  </span>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <CalendarIcon className="w-12 h-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 font-medium">No upcoming appointments</p>
              <p className="text-sm text-slate-500 mt-1">
                Scheduled appointments will appear here
              </p>
              <Link to="/admin/appointments">
                <Button variant="outline" size="sm" className="mt-4 border-slate-700 text-slate-300">
                  Manage Appointments
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Appointment Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-lg bg-navy-900 border-slate-700 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
              <Edit className="w-5 h-5 text-primary" />
              Edit Appointment
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {editingAppointment && getContactInfo(editingAppointment.lead_id, editingAppointment.client_id).name} - {editingAppointment && getContactInfo(editingAppointment.lead_id, editingAppointment.client_id).business}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Date */}
            <div className="space-y-2">
              <Label className="text-slate-300">Date</Label>
              <div className="bg-navy-950 rounded-lg p-2 border border-slate-700">
                <Calendar
                  mode="single"
                  selected={editDate}
                  onSelect={setEditDate}
                  className="rounded-md"
                  disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                />
              </div>
            </div>

            {/* Time */}
            <div className="space-y-2">
              <Label className="text-slate-300">Time</Label>
              <Select value={editTime} onValueChange={setEditTime}>
                <SelectTrigger className="bg-navy-950 border-slate-700 text-white">
                  <SelectValue placeholder="Select time" />
                </SelectTrigger>
                <SelectContent className="bg-navy-900 border-slate-700 max-h-60">
                  {timesForEditDate.length > 0 ? (
                    timesForEditDate.map((time) => (
                      <SelectItem key={time} value={time} className="text-white hover:bg-navy-800">
                        {formatTime12Hour(time)}
                      </SelectItem>
                    ))
                  ) : (
                    <SelectItem value={editTime} className="text-white hover:bg-navy-800">
                      {formatTime12Hour(editTime)}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Duration */}
            <div className="space-y-2">
              <Label className="text-slate-300">Duration</Label>
              <Select value={String(editDuration)} onValueChange={(v) => setEditDuration(Number(v))}>
                <SelectTrigger className="bg-navy-950 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-navy-900 border-slate-700">
                  <SelectItem value="30" className="text-white hover:bg-navy-800">30 minutes</SelectItem>
                  <SelectItem value="60" className="text-white hover:bg-navy-800">60 minutes</SelectItem>
                  <SelectItem value="90" className="text-white hover:bg-navy-800">90 minutes</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Meeting Format (Zoom/In-Person) */}
            <div className="space-y-2">
              <Label className="text-slate-300">Meeting Format</Label>
              <Select value={editMeetingFormat} onValueChange={setEditMeetingFormat}>
                <SelectTrigger className="bg-navy-950 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-navy-900 border-slate-700">
                  <SelectItem value="zoom" className="text-white hover:bg-navy-800">
                    <span className="flex items-center gap-2">
                      <Video className="w-4 h-4" /> Zoom
                    </span>
                  </SelectItem>
                  <SelectItem value="in_person" className="text-white hover:bg-navy-800">
                    <span className="flex items-center gap-2">
                      <Building className="w-4 h-4" /> In-Person
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Meeting Type */}
            <div className="space-y-2">
              <Label className="text-slate-300">Meeting Type</Label>
              <Select value={editMeetingType} onValueChange={setEditMeetingType}>
                <SelectTrigger className="bg-navy-950 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-navy-900 border-slate-700">
                  {Object.entries(MEETING_TYPES).map(([key, { label }]) => (
                    <SelectItem key={key} value={key} className="text-white hover:bg-navy-800">
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status */}
            <div className="space-y-2">
              <Label className="text-slate-300">Status</Label>
              <Select value={editStatus} onValueChange={setEditStatus}>
                <SelectTrigger className="bg-navy-950 border-slate-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-navy-900 border-slate-700">
                  <SelectItem value="scheduled" className="text-white hover:bg-navy-800">Scheduled</SelectItem>
                  <SelectItem value="completed" className="text-white hover:bg-navy-800">Completed</SelectItem>
                  <SelectItem value="canceled" className="text-white hover:bg-navy-800">Canceled</SelectItem>
                  <SelectItem value="no_show" className="text-white hover:bg-navy-800">No Show</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-between gap-3 mt-6 pt-4 border-t border-slate-700">
            <Button
              variant="ghost"
              onClick={() => setDeleteModalOpen(true)}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setEditModalOpen(false)}
                className="border-slate-700 text-slate-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdate}
                disabled={isSubmitting}
                className="bg-primary hover:bg-primary/90"
              >
                {isSubmitting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="max-w-sm bg-navy-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Delete Appointment</DialogTitle>
            <DialogDescription className="text-slate-400">
              Are you sure you want to delete this appointment? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-3 mt-4">
            <Button
              variant="outline"
              onClick={() => setDeleteModalOpen(false)}
              className="border-slate-700 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={isSubmitting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
